import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, CreditCard, X } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionExpiryModal() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/auth/subscription-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const days = data.days_remaining;
        setDaysRemaining(days);
        setExpiryDate(data.trial_end);

        // Verificar se deve mostrar o modal (3 dias ou 1 dia antes)
        // Não mostrar se já expirou (days <= 0) pois já tem outra tela para isso
        if (days > 0 && days <= 3) {
          // Verificar se o usuário já fechou o modal hoje
          const lastDismissed = localStorage.getItem('subscriptionModalDismissed');
          const today = new Date().toDateString();
          
          if (lastDismissed !== today) {
            setShowModal(true);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao verificar assinatura:', error);
    }
  };

  const handleDismiss = () => {
    // Salvar que o usuário fechou o modal hoje
    localStorage.setItem('subscriptionModalDismissed', new Date().toDateString());
    setShowModal(false);
  };

  const handleRenew = () => {
    setShowModal(false);
    navigate('/plans');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  if (!showModal || daysRemaining === null) return null;

  const isUrgent = daysRemaining <= 1;

  return (
    <Dialog open={showModal} onOpenChange={setShowModal}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md p-0 overflow-hidden">
        {/* Header colorido */}
        <div className={`p-6 ${isUrgent ? 'bg-red-600' : 'bg-amber-600'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-full">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {isUrgent ? 'Atenção!' : 'Aviso Importante'}
                </h2>
                <p className="text-sm text-white/80">Sua assinatura está expirando</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {/* Contador de dias */}
          <div className={`text-center p-6 rounded-xl mb-6 ${
            isUrgent ? 'bg-red-500/20 border border-red-500/30' : 'bg-amber-500/20 border border-amber-500/30'
          }`}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className={`w-6 h-6 ${isUrgent ? 'text-red-400' : 'text-amber-400'}`} />
              <span className={`text-sm font-medium ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>
                Tempo restante
              </span>
            </div>
            <p className={`text-5xl font-bold mb-1 ${isUrgent ? 'text-red-400' : 'text-amber-400'}`}>
              {daysRemaining}
            </p>
            <p className={`text-lg ${isUrgent ? 'text-red-300' : 'text-amber-300'}`}>
              {daysRemaining === 1 ? 'dia restante' : 'dias restantes'}
            </p>
          </div>

          {/* Mensagem */}
          <div className="mb-6">
            <p className="text-slate-300 text-center">
              {isUrgent ? (
                <>
                  <strong className="text-red-400">Sua assinatura expira amanhã!</strong>
                  <br />
                  Renove agora para não perder o acesso ao sistema.
                </>
              ) : (
                <>
                  Sua assinatura expira em <strong className="text-amber-400">{formatDate(expiryDate)}</strong>.
                  <br />
                  Renove com antecedência para garantir seu acesso.
                </>
              )}
            </p>
          </div>

          {/* Botões */}
          <div className="space-y-3">
            <Button
              onClick={handleRenew}
              className={`w-full py-6 text-lg font-semibold ${
                isUrgent 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Renovar Agora
            </Button>
            
            <button
              onClick={handleDismiss}
              className="w-full py-3 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Lembrar mais tarde
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
